import {useState} from 'react';
import {useSelector} from 'react-redux';

import type {User} from 'nutshell-core/users/users-types';

import {Stack} from 'shells/layout/stack/stack';

import type {ContactPageFragment} from 'nutshell-graphql-types';

import {CustomizeLayoutButton} from '../../../core-entity/layout-customizer/customize-layout-button';
import {MiniActionBar} from '../../../core-entity/mini-header/mini-action-bar';
import {MiniHeader} from '../../../core-entity/mini-header/mini-header';
import {TAB_CALL_MINI_FIXED_ANCHOR} from '../../../core-entity/redux/consts';
import {getCoreEntityPageActionBarTab} from '../../../core-entity/redux/core-entity-page-selectors';
import {
    getTabOptions,
    useHandleCallClick,
    useOpenEmailModal,
    useOpenTextModal,
} from '../../../core-entity/util/action-bar-helpers';
import {PeepAssigneeSelect} from '../../../entity-details/peep-details/peep-assignee-select';
import {PeepMiniHeaderTitle} from '../../../peep/shared/peep-mini-header-title';

import {PeepHeaderActionMenu} from '../../shared/header/action-bar/peep-header-action-menu';

import {ContactContextOverview} from '../header/contact-context-overview';
import {useGetSmsEmailUnsubscribes} from '../sidebar/audiences-widget/graphql/hooks/use-get-sms-email-unsubscribes';

import styles from '../../peep-page.module.css';

type Props = {
    isShown: boolean;
    contentRef: {
        current: HTMLDivElement | null;
    };
    contact: ContactPageFragment & {
        hasUploadedAvatar?: boolean | null;
    };
    user: User;
    scrollContainerTop: number;
    onUpdateAssignee: (assigneeId: string | null) => Promise<any>;
    onUpdateDeletedStatus: (isDeleted: boolean) => Promise<any>;
    onHardDelete: () => Promise<any>;
};

export function ContactHeaderMiniOnScroll(props: Props) {
    const [isUpdatingAssignee, setIsUpdatingAssignee] = useState<boolean>(false);

    const {isUnsubscribed, isLoading} = useGetSmsEmailUnsubscribes(props.contact.id);
    const actionBarTab = useSelector(getCoreEntityPageActionBarTab);

    const openEmailModal = useOpenEmailModal(props.contact);
    const {openTextModal} = useOpenTextModal(props.contact, props.user);
    const {handleCallClick, popoverComponent} = useHandleCallClick(
        TAB_CALL_MINI_FIXED_ANCHOR,
        props.contact.id,
        {
            noPhoneNumbersQuestion: 'No phone numbers associated with this person',
        }
    );

    const handleUpdateAssignee = (assigneeId?: string | null | undefined) => {
        if (isUpdatingAssignee) {
            return;
        }

        setIsUpdatingAssignee(true);
        props.onUpdateAssignee(assigneeId ?? null).finally(() => {
            setIsUpdatingAssignee(false);
        });
    };

    const options = getTabOptions(openEmailModal, openTextModal, handleCallClick);

    return (
        <MiniHeader
            isShown={props.isShown}
            contentRef={props.contentRef}
            scrollContainerTop={props.scrollContainerTop}
            entityType='peep'
        >
            <Stack spacing={8} isFullWidth={true}>
                <Stack
                    isHorizontal={true}
                    spacing={8}
                    justifyContent='space-between'
                    isFullWidth={true}
                    alignItems='start'
                >
                    <PeepMiniHeaderTitle
                        avatarUrl={props.contact.avatarUrl}
                        hasUploadedAvatar={Boolean(props.contact.hasUploadedAvatar)}
                        avatarType='contacts'
                        name={props.contact.name}
                        contextOverview={<ContactContextOverview contact={props.contact} />}
                        isDeleted={props.contact.isDeleted ?? false}
                    />
                    <Stack isHorizontal={true} spacing={8} alignItems='center'>
                        <div className={styles['assignee-select-container']}>
                            <PeepAssigneeSelect
                                assigneeId={props.contact.owner?.id ?? null}
                                onUpdatePeepAssignee={handleUpdateAssignee}
                                withControlIcon={false}
                                placeholder='Assign this person…'
                                isDisabled={
                                    !props.user.permissions.canAssignEntities || isUpdatingAssignee
                                }
                            />
                        </div>
                        {props.user.permissions.canAccessSetup && (
                            <CustomizeLayoutButton text='Layout' entityType={'CONTACT'} />
                        )}
                        <PeepHeaderActionMenu
                            entityType='contact'
                            entityId={props.contact.id}
                            entityName={props.contact.name}
                            isSoftDeleted={props.contact.isDeleted ?? false}
                            onDelete={() => props.onUpdateDeletedStatus(true)}
                            onUndelete={() => props.onUpdateDeletedStatus(false)}
                            onPermanentlyDelete={props.onHardDelete}
                            isUnsubscribedFromAll={!isLoading ? isUnsubscribed : undefined}
                        />
                    </Stack>
                </Stack>
                <MiniActionBar
                    entityId={props.contact.id}
                    entityName={'Contacts'}
                    entityDisplayName={props.contact.name}
                    options={options}
                    selectedTab={actionBarTab}
                    isShown={props.isShown}
                />
                {popoverComponent}
            </Stack>
        </MiniHeader>
    );
}
